import {
  createStore,
  GlobalState,
  Master,
  useStateMachine,
} from 'little-state-machine';

import { env } from '../framework/utils/EnvUtils';
import keycloak from '../framework/utils/KeycloakUtils';
import { ObjectType, TypeUtils } from '../framework/utils/UtilityTypes';
import { pagePaths, Pages } from './constants/Page';
import { EngeneNavigateFunction } from './hooks/navigate';
import { MasterSearchResult } from './hooks/queries/master';
import { AppConfig } from './models/AppConfig';
import { PersonalInfoAgreeGetResult } from './models/gen/models';
import { SettingsGetResult } from './models/gen/settingsGetResult';
import { UserInfoAccessor } from './utils/UserInfoUtils';

const STORAGE = sessionStorage;
const STORE_KEY = 'SyllabusUI';
const INITIAL_GLOBAL_STORE: GlobalState = {
  appConfig: {
    batchRegisterMaxSize: 500, // default 値 (TODO 不要なら消す)
  },
  isGuarding: false, // 画面ガード(連投抑止)
  pageInfo: {},
};

const log = (store: GlobalState) => {
  console.log(store);
  return store;
};

const createGlobalStore = () =>
  createStore(INITIAL_GLOBAL_STORE, {
    name: STORE_KEY,
    middleWares: [log],
    storageType: STORAGE,
  });

const initializeGlobalStore = (
  _state: GlobalState,
  _payload: GlobalState
): GlobalState => INITIAL_GLOBAL_STORE;

const eraseGlobalStore = () => {
  STORAGE.removeItem(STORE_KEY);
};

export const GlobalStoreInitializer = {
  createGlobalStore: createGlobalStore,
  eraseGlobalStore: eraseGlobalStore,
};

const updateAppConfig = (
  state: GlobalState,
  payload: AppConfig
): GlobalState => ({
  ...state,
  appConfig: payload,
});

const setGuard = (state: GlobalState, payload: boolean): GlobalState => ({
  ...state,
  isGuarding: payload,
});

const updatePageInfo = (
  state: GlobalState,
  payload: { [K in Pages]?: ObjectType }
): GlobalState => {
  const store = {
    ...state,
  };
  Object.keys(payload).forEach(
    (k) =>
      (store.pageInfo[k as Pages] = {
        ...store.pageInfo[k as Pages],
        ...payload[k as Pages],
      })
  );
  return store;
};

const clearAllPageInfo = (
  state: GlobalState,
  _payload: EmptyObject
): GlobalState => ({
  ...state,
  pageInfo: {},
});

const clearPageInfo = (state: GlobalState, payload: Pages): GlobalState => {
  const store = {
    ...state,
  };
  store.pageInfo[payload] = undefined;
  return store;
};

const updateUserInfo = (state: GlobalState): GlobalState => {
  UserInfoAccessor.setUserInfo();

  return { ...state, userInfo: UserInfoAccessor.getUserInfo() };
};

const updateMaster = (
  state: GlobalState,
  payload: Record<string, Array<Master>>
) => ({ ...state, master: payload });

const setAgreeRequired = (state: GlobalState, payload?: boolean) => ({
  ...state,
  agreeRequired: TypeUtils.isNil(payload) ? true : payload,
});

const login = (state: GlobalState): GlobalState => {
  if (state.isLoggedIn) return state;
  console.debug('Login');

  keycloak.login({ redirectUri: `${env.APP_ROOT}` });

  return { ...state, isLoggedIn: true, userInfo: undefined };
};

const setGlobalData = (
  state: GlobalState,
  // callback: Consumer<UserInfo | undefined>
  [agree, facilitySettings, master]: [
    PersonalInfoAgreeGetResult,
    SettingsGetResult,
    MasterSearchResult
  ]
): GlobalState => {
  // UserInfoAccessor.setUserInfo();
  const userInfo = UserInfoAccessor.getUserInfo();
  // callback(userInfo);

  if (userInfo?.roleOptions.isEnabledToLogin) {
    return {
      ...state,
      agreeRequired: agree.personalInfoAgree,
      appConfig: {
        ...state.appConfig,
        facilitySettings: Object.entries(
          facilitySettings?.results || {}
        ).reduce(
          (prev, [curK, curV]) => ({
            ...prev,
            [curK]: /^\d{4}-\d{2}-\d{2}$/.test(curV as string)
              ? curV
              : JSON.parse(curV),
          }),
          {}
        ),
      },
      userInfo: userInfo,
      master: master,
    };
  } else return state;
};

const logout = (
  state: GlobalState
  // navigate: EngeneNavigateFunction
): GlobalState => {
  // if (!state.isLoggedIn) {
  //   navigate(pagePaths.ROOT);
  //   return state;
  // }
  console.debug('Logout');

  keycloak.logout({ redirectUri: env.APP_ROOT });

  return {
    ...state,
    isLoggedIn: false,
    pageInfo: {},
    appConfig: {
      ...state.appConfig,
      applicationSettings: undefined,
      facilitySettings: undefined,
    },
  };
};

export const useGlobalStateMachine = () =>
  useStateMachine({
    initializeGlobalStore: initializeGlobalStore,
    updateAppConfig: updateAppConfig,
    setGuard: setGuard,
    updatePageInfo: updatePageInfo,
    clearAllPageInfo: clearAllPageInfo,
    clearPageInfo: clearPageInfo,
    updateUserInfo: updateUserInfo,
    updateMaster: updateMaster,
    setAgreeRequired: setAgreeRequired,
    login: login,
    logout: logout,
    setGlobalData: setGlobalData,
  });
